import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, catchError, map, of, switchMap, tap, throwError } from 'rxjs';
import type { AdminSystemUser } from '../../../../domain/src/lib/admin-users/admin-user.model';
import type {
  ChangePasswordRequest,
  ProfileSelfUpdatePayload,
  UserMeResponse,
} from '../../../../domain/src/lib/user-profile/user-profile.model';
import {
  persistVaultOperativeRoleFromAuthMe,
  reconcileVaultActiveTenantFromAuthMe,
} from '@nebula/shared-vault/data-access';
import { AdminUsersService } from '../admin-users/admin-users.service';
import { unwrapVaultApiRecord } from '../shared/tenant-context.util';

@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly http = inject(HttpClient);
  private readonly adminUsers = inject(AdminUsersService);

  getMe(): Observable<UserMeResponse> {
    return this.http.get<unknown>('/api/auth/me').pipe(
      map((raw) => (unwrapVaultApiRecord(raw) ?? raw) as UserMeResponse),
      tap((me) => {
        persistVaultOperativeRoleFromAuthMe(me);
        reconcileVaultActiveTenantFromAuthMe(me);
      }),
    );
  }

  /**
   * Perfil para el formulario: `auth/me` + hidratación de `full_name` desde
   * `GET /api/admin/users/{id}` (misma fuente que Gestión de usuarios).
   */
  loadProfile(): Observable<UserMeResponse> {
    return this.getMe().pipe(
      switchMap((me) => {
        const userId = String(me.id ?? '').trim();
        if (!userId) return of(me);
        return this.adminUsers.get(userId).pipe(
          map((user) => this.mergeMeWithAdminUser(me, user)),
          catchError(() => of(me)),
        );
      }),
    );
  }

  /**
   * Nombre para mostrar: mismo contrato que editar en Gestión de usuarios
   * (`PUT /api/admin/users/{id}`). DEV aún no monta `PUT /api/auth/profile`.
   */
  saveDisplayName(
    userId: string,
    data: Pick<ProfileSelfUpdatePayload, 'full_name' | 'email' | 'is_active'>,
  ): Observable<AdminSystemUser> {
    return this.adminUsers.update(userId, {
      full_name: data.full_name,
      email: data.email,
      is_active: data.is_active ?? true,
    });
  }

  saveProfileWithAdminPasswordReset(
    userId: string,
    data: ProfileSelfUpdatePayload,
  ): Observable<AdminSystemUser> {
    const payload = {
      full_name: data.full_name,
      email: data.email,
      is_active: data.is_active ?? true,
      ...(data.password?.trim()
        ? { password: data.password.trim(), current_password: '' as const }
        : {}),
    };
    return this.adminUsers.update(userId, payload);
  }

  changePassword(userId: string, body: ChangePasswordRequest): Observable<void> {
    return this.http.put<void>('/api/auth/change-password', body).pipe(
      catchError((err: { status?: number }) =>
        err?.status === 404
          ? this.adminUsers
              .update(userId, {
                password: body.new_password,
                current_password: body.current_password,
              })
              .pipe(map(() => undefined))
          : throwError(() => err),
      ),
    );
  }

  saveProfileChanges(options: {
    userId: string;
    data: ProfileSelfUpdatePayload;
    adminPasswordReset: boolean;
    currentPassword?: string;
    nameChanged: boolean;
    wantsPasswordChange: boolean;
  }): Observable<AdminSystemUser | void> {
    const { userId, data, adminPasswordReset, currentPassword, nameChanged, wantsPasswordChange } =
      options;

    if (adminPasswordReset) {
      return this.saveProfileWithAdminPasswordReset(userId, {
        ...data,
        ...(wantsPasswordChange ? { password: data.password } : {}),
      });
    }

    const cur = currentPassword?.trim() ?? '';
    const next = data.password?.trim() ?? '';

    if (wantsPasswordChange && nameChanged) {
      return this.adminUsers.update(userId, {
        full_name: data.full_name,
        email: data.email,
        is_active: data.is_active ?? true,
        password: next,
        current_password: cur,
      });
    }

    if (wantsPasswordChange) {
      return this.changePassword(userId, { current_password: cur, new_password: next }).pipe(
        map(() => undefined),
      );
    }

    if (nameChanged) {
      return this.saveDisplayName(userId, data);
    }

    return of(undefined);
  }

  getMyTenants(): Observable<unknown> {
    return this.http.get<unknown>('/api/tenants/my-tenants');
  }

  private mergeMeWithAdminUser(me: UserMeResponse, user: AdminSystemUser): UserMeResponse {
    const full_name = user.full_name?.trim() || me.full_name || me.name;
    return {
      ...me,
      email: user.email?.trim() || me.email,
      full_name,
      name: full_name,
      is_active: user.is_active ?? me.is_active,
    };
  }
}
